// Flow 1 - Talk-to-Glim guided intake (docs/phase-0/slice5-ux-flows.md §4.1-4.2). Route
// `/apps/new/glim` (wired by Wave 4, not this file - `RequireWizardApp` guarantees
// `draft.appId` is set before this screen ever renders). Reuses `.chat-thread`/
// `.msg-bubble`/`.composer-row` wholesale (docs/phase-0/slice5-component-designs.md §3.1 -
// "not a new visual component... AskCrew.tsx's own composition reused wholesale").
//
// NO CHAT ENDPOINT EXISTS on `VALIDATE_ROUTES` (packages/shared/src/http/validate.ts:
// 31-40 - confirmed by grep, no "chat" path). This lane does not invent one: the
// transcript is held CLIENT-SIDE only (never persisted across a real reload - flagged,
// ASSUMED simplification), and Glim's guiding replies are a small canned rotation, not a
// live model call. The one real network write this screen makes is the canonical
// `POST /v1/apps/:appId/validate/verdict`, sent once the founder's collected brief looks
// long enough to be worth researching (a client-side length heuristic, ASSUMED - no
// server-side readiness signal exists to defer to instead).
import { useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isErr } from "@nightcrew/shared";
import { FocusedFlowShell } from "../../components/FocusedFlowShell.js";
import { DeniedCard } from "../../components/DeniedCard.js";
import { UploadContextPanel } from "../../components/UploadContextPanel.js";
import { Field } from "../../components/Field.js";
import { getApp } from "../../api/apps.js";
import { requestVerdict } from "../../api/validate.js";
import { isBoundaryCode } from "../../api/errors.js";
import { isOwnerOrAdmin, useSession } from "../../state/session.js";
import { useWizard } from "../../state/wizard.js";
import { validateCopy } from "../../copy/validate.js";
import glimIllustration from "../../assets/glim.svg";

/** The client-side "is this worth researching" heuristic (ASSUMED, flagged - see this
 * file's own header comment): the same order of magnitude as `IntentStep.tsx`'s own
 * free-text validity floor (`freeText.trim().length >= 10`), a bit higher since a verdict
 * research run is a heavier action than a plain intent free-text field. */
const READY_LENGTH_FLOOR = 20;

const EXAMPLE_CHIPS = [
  "validate.glim.example1",
  "validate.glim.example2",
  "validate.glim.example3",
] as const;

/** A small, fixed rotation of guiding follow-ups - plain client-side copy, never a live
 * model call (see header comment). Cycles once every example is used. */
const GLIM_FOLLOW_UPS = [
  "Tell me more - who is this for, and what do they do today instead?",
  "Good. What would make someone pay for this instead of a free alternative?",
  "I think we have enough to get you a real verdict. Ready when you are.",
] as const;

interface Turn {
  readonly id: string;
  readonly author: "founder" | "glim";
  readonly text: string;
}

type AppLoad =
  | { readonly status: "loading" }
  | { readonly status: "denied" }
  | { readonly status: "error" }
  | { readonly status: "ready"; readonly name: string };

export const TalkToGlim = (): React.JSX.Element => {
  const navigate = useNavigate();
  const { draft } = useWizard();
  const { state: session } = useSession();
  const [appLoad, setAppLoad] = useState<AppLoad>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);
  const [messages, setMessages] = useState<readonly Turn[]>([]);
  const [composerText, setComposerText] = useState("");
  const [showUpload, setShowUpload] = useState(false);
  const [verdictPending, setVerdictPending] = useState(false);
  const [verdictError, setVerdictError] = useState<string | null>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const composerId = useId();
  const hintId = useId();

  const appId = draft.appId;

  // Loads the app once, purely to read its name and to detect a stale/cross-tenant id -
  // `getApp` is the one existing, tenant-scoped read every other screen already trusts
  // for this (AppDetail.tsx's own not_found/forbidden -> DeniedCard pattern).
  useEffect(() => {
    if (appId === null) return;
    const cancelled = { current: false };
    setAppLoad({ status: "loading" });
    void (async () => {
      const result = await getApp(appId);
      if (cancelled.current) return;
      if (isErr(result)) {
        const denied =
          isBoundaryCode(result.error, "not_found") || isBoundaryCode(result.error, "forbidden");
        setAppLoad(denied ? { status: "denied" } : { status: "error" });
        return;
      }
      setAppLoad({ status: "ready", name: result.value.name });
    })();
    return () => {
      cancelled.current = true;
    };
  }, [appId, attempt]);

  if (appId === null) {
    return (
      <FocusedFlowShell title={validateCopy("validate.glim.heading")}>
        <div className="stack gap12">
          <div className="skeleton skeleton-line" style={{ width: "40%" }} />
          <div className="card skeleton-card" />
        </div>
      </FocusedFlowShell>
    );
  }

  if (!isOwnerOrAdmin(session)) {
    return (
      <FocusedFlowShell
        title={validateCopy("validate.glim.heading")}
        onBack={() => {
          void navigate("/apps/new");
        }}
      >
        <DeniedCard message={validateCopy("validate.glim.deniedNotOwnerAdmin")} />
      </FocusedFlowShell>
    );
  }

  if (appLoad.status === "denied") {
    return (
      <FocusedFlowShell title={validateCopy("validate.glim.heading")}>
        <DeniedCard
          title="We can't find that app"
          message="It may not exist, or it isn't on your account."
        />
      </FocusedFlowShell>
    );
  }

  if (appLoad.status === "loading") {
    return (
      <FocusedFlowShell
        title={validateCopy("validate.glim.heading")}
        onBack={() => {
          void navigate("/apps/new");
        }}
      >
        <div className="stack gap12">
          <div className="skeleton skeleton-line" style={{ width: "40%" }} />
          <div className="card skeleton-card" />
        </div>
      </FocusedFlowShell>
    );
  }

  if (appLoad.status === "error") {
    return (
      <FocusedFlowShell
        title={validateCopy("validate.glim.heading")}
        onBack={() => {
          void navigate("/apps/new");
        }}
      >
        <div className="card state-card">
          <p>{validateCopy("validate.glim.loadFailed")}</p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              setAttempt((a) => a + 1);
            }}
          >
            {validateCopy("validate.glim.retryCta")}
          </button>
        </div>
      </FocusedFlowShell>
    );
  }

  const founderBriefLength = messages
    .filter((m) => m.author === "founder")
    .reduce((total, m) => total + m.text.trim().length, 0);
  const canRequestVerdict = founderBriefLength >= READY_LENGTH_FLOOR;

  const appendFounderTurn = (text: string): void => {
    const trimmed = text.trim();
    if (trimmed.length === 0) return;
    const founderId = `${String(Date.now())}-founder`;
    setMessages((prev) => {
      const glimIndex = Math.min(prev.filter((m) => m.author === "glim").length, GLIM_FOLLOW_UPS.length - 1);
      const glimTurn: Turn = {
        id: `${String(Date.now())}-glim`,
        author: "glim",
        text: GLIM_FOLLOW_UPS[glimIndex] ?? "I think we have enough to get you a real verdict. Ready when you are.",
      };
      return [...prev, { id: founderId, author: "founder", text: trimmed }, glimTurn];
    });
  };

  const handleSend = (): void => {
    appendFounderTurn(composerText);
    setComposerText("");
    composerRef.current?.focus();
  };

  const handleGetVerdict = (): void => {
    if (!canRequestVerdict || verdictPending) return;
    const brief = messages
      .filter((m) => m.author === "founder")
      .map((m) => m.text)
      .join("\n\n");
    setVerdictPending(true);
    setVerdictError(null);
    void (async () => {
      const result = await requestVerdict(appId, brief);
      setVerdictPending(false);
      if (isErr(result)) {
        setVerdictError(validateCopy("validate.glim.sendError"));
        return;
      }
      void navigate("/apps/new/glim/verdict");
    })();
  };

  return (
    <FocusedFlowShell
      title={validateCopy("validate.glim.heading")}
      onBack={() => {
        void navigate("/apps/new");
      }}
      cta={
        <div className="stack gap8">
          {verdictError !== null ? (
            <p className="field-error" role="alert">
              {verdictError}
            </p>
          ) : null}
          {!canRequestVerdict ? (
            <p id={hintId} className="muted" style={{ fontSize: 12 }}>
              {validateCopy("validate.glim.notReadyPrefix", {
                gap: "tell Glim a bit more about the idea",
              })}
            </p>
          ) : null}
          <button
            type="button"
            className="btn btn-primary"
            disabled={!canRequestVerdict || verdictPending}
            aria-describedby={!canRequestVerdict ? hintId : undefined}
            onClick={handleGetVerdict}
          >
            {verdictPending
              ? validateCopy("validate.glim.gettingVerdict")
              : validateCopy("validate.glim.getVerdictCta")}
          </button>
        </div>
      }
    >
      <div className={showUpload ? "glim-flow-layout" : undefined}>
        <div className="card pad">
          <div className="row between aic gap8">
            <h1 className="h2">{validateCopy("validate.glim.heading")}</h1>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={() => {
                setShowUpload((open) => !open);
              }}
            >
              {validateCopy("validate.glim.addContextCta")}
            </button>
          </div>

          <div className="chat-thread mt16" role="log" aria-label="Conversation with Glim">
            {messages.length === 0 ? (
              <div className="msg-bubble crew">
                <div className="msg-who">
                  <img src={glimIllustration} alt="" width={16} height={16} aria-hidden="true" />
                  Glim, your crew
                </div>
                <p style={{ margin: 0 }}>{validateCopy("validate.glim.emptyPrompt")}</p>
                <div className="row wrap-g gap8 mt8">
                  {EXAMPLE_CHIPS.map((key) => (
                    <button
                      key={key}
                      type="button"
                      className="qchip"
                      onClick={() => {
                        setComposerText(validateCopy(key));
                      }}
                    >
                      {validateCopy(key)}
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map((turn) =>
                turn.author === "founder" ? (
                  <div key={turn.id} className="msg-bubble me">
                    <span className="sr-only">You: </span>
                    {turn.text}
                  </div>
                ) : (
                  <div key={turn.id} className="msg-bubble crew">
                    <div className="msg-who">
                      <img src={glimIllustration} alt="" width={16} height={16} aria-hidden="true" />
                      Glim, your crew
                    </div>
                    <p style={{ margin: 0 }}>{turn.text}</p>
                  </div>
                ),
              )
            )}
          </div>

          <div className="composer-row">
            <div className="field-wrap">
              <Field
                id={composerId}
                inputRef={composerRef}
                label={validateCopy("validate.glim.composerLabel")}
                hideLabel
                multiline
                rows={2}
                value={composerText}
                placeholder={validateCopy("validate.glim.emptyPrompt")}
                onChange={setComposerText}
              />
            </div>
            <button
              type="button"
              className="btn btn-primary"
              disabled={composerText.trim().length === 0}
              onClick={handleSend}
            >
              {validateCopy("validate.glim.sendCta")}
            </button>
          </div>
        </div>

        {showUpload ? (
          <UploadContextPanel
            appId={appId}
            canManage={isOwnerOrAdmin(session)}
            onDone={() => {
              setShowUpload(false);
            }}
          />
        ) : null}
      </div>
    </FocusedFlowShell>
  );
};
